import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  KeyRound, Palette, Tags, Landmark, ReceiptText, FileCog, Megaphone, ScrollText,
  Activity, Building, Globe2, ClipboardCheck, Wallet, LineChart,
  BadgeDollarSign, TrendingUp, CalendarClock, UserCog, ChevronLeft, ChevronRight,
} from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useBreakpoint } from "@/lib/useBreakpoint";
import { WS_TOKENS } from "@/lib/workspaceTheme";
import PortalControls from "@/components/PortalControls";

/**
 * AdminSettingsPage — one clean settings hub for STAS / managers / employees.
 *
 * Design: rows (never cards). Grouped by area. Each row is a tap-target
 * with icon + title + brief description + chevron. No decoration bloat.
 * All destinations are pre-existing routes.
 */
export default function AdminSettingsPage() {
  const { user } = useAuth();
  const { theme, lang, dir } = useI18n();
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const [moduleVis, setModuleVis] = useState({ attendance: true });

  useEffect(() => {
    if (!user) return;
    api.get("/module-visibility").then((r) => r.data).then((mv) => mv && setModuleVis(mv)).catch(() => {});
  }, [user]);

  if (!user) return null;

  const isRoot = !!user.role?.is_root;
  const roleCode = user.role?.code;
  const isManager = ["general_manager", "deputy_manager", "accountant"].includes(roleCode);
  const isEmployee = roleCode === "employee";
  const isAccountant = roleCode === "accountant";

  const ChevronIcon = dir === "rtl" ? ChevronLeft : ChevronRight;

  // ── Groups ──
  const groups = [];

  if (isRoot) {
    groups.push({
      key: "system", title: lang === "ar" ? "النظام" : "System",
      items: [
        { icon: KeyRound,   to: "/admin/users",                title: lang === "ar" ? "المستخدمون والصلاحيات" : "Users & Access",       desc: lang === "ar" ? "الإدارة، الموظفون، العملاء" : "Admin, employees, clients",           testId: "settings-users" },
        { icon: Palette,    to: "/admin/branding",             title: lang === "ar" ? "الهوية والعلامة" : "Branding",                  desc: lang === "ar" ? "الشعار، الاسم، الألوان" : "Logo, name, colors",                       testId: "settings-branding" },
        { icon: ScrollText, to: "/admin/policies",             title: lang === "ar" ? "السياسات الرسمية" : "Official Policies",         desc: lang === "ar" ? "10% استرداد، شروط الخدمة، الخصوصية…" : "Refund, service, privacy…",     testId: "settings-policies" },
        { icon: Activity,   to: "/admin/system-health",        title: lang === "ar" ? "صحة النظام" : "System Health",                   desc: lang === "ar" ? "حالة DB، الأداء، نشر التحديث" : "DB status, force-update",           testId: "settings-health" },
        { icon: Globe2,     to: "/admin/countries",            title: lang === "ar" ? "الدول" : "Countries",                            desc: lang === "ar" ? "تفعيل/حظر، الأسماء والمرادفات" : "Enable / block / aliases",         testId: "settings-countries" },
      ],
    });
    groups.push({
      key: "ops", title: lang === "ar" ? "العمليات" : "Operations",
      items: [
        { icon: Tags,       to: "/admin/pricing-catalog",      title: lang === "ar" ? "كتالوج بنود التسعير" : "Pricing Catalog",       desc: lang === "ar" ? "البنود التي يستخدمها المدراء في الفواتير" : "Line items used in invoices", testId: "settings-catalog" },
        { icon: Tags, to: "/admin/promo-codes", title: lang === "ar" ? "أكواد الخصم والتسويق" : "Promo Codes", desc: lang === "ar" ? "الخصم والعمولة وتتبع الاستخدام" : "Discounts, commissions and usage", testId: "settings-promo-codes" },
        { icon: Landmark,   to: "/admin/banks",                title: lang === "ar" ? "البنوك المعتمدة" : "Approved Banks",             desc: lang === "ar" ? "الراجحي، الإنماء، STC Bank…" : "Al Rajhi, Al Inma, STC…",             testId: "settings-banks" },
        { icon: ReceiptText, to: "/admin/receipt-template",    title: lang === "ar" ? "قالب الإيصال" : "Receipt Template",              desc: lang === "ar" ? "الرأس، الذيل، الختم، الرقم الضريبي" : "Header, footer, seal, VAT",   testId: "settings-receipt-template" },
        { icon: FileCog,    to: "/admin/document-requirements",title: lang === "ar" ? "متطلبات المستندات" : "Document Requirements",   desc: lang === "ar" ? "قوائم المتطلبات للمواطن والمقيم" : "Citizen / resident checklists",  testId: "settings-doc-reqs" },
        { icon: Building,   to: "/admin/muaqqibs",             title: lang === "ar" ? "دليل المعقّبين" : "Muaqqib Directory",           desc: lang === "ar" ? "إدارة المعقّبين وربطهم بالمعاملات" : "Manage muaqqibs",                testId: "settings-muaqqib" },
        { icon: Megaphone,  to: "/admin/portal-campaigns",     title: lang === "ar" ? "إعلانات البوابة" : "Portal Campaigns",           desc: lang === "ar" ? "بنرات، عروض، وفيديوهات في البوابة" : "Banners, offers, videos",       testId: "settings-portal-campaigns" },
      ],
    });
  }

  // Finance + Performance + Bonus — STAS, managers, accountant
  // NOTE: "لوحة المحاسب" و"تقييم الأداء" الحين في الشريط الجانبي مباشرة.
  // نُبقيهما هنا كوصول احتياطي مع سياسة البونص لأنها إعدادات نظام.
  if (isRoot || isManager) {
    groups.push({
      key: "finance", title: lang === "ar" ? "إعدادات المالية والأداء" : "Finance & Performance Settings",
      items: [
        { icon: BadgeDollarSign, to: "/admin/bonus-policy",   title: lang === "ar" ? "سياسة البونص الشهرية" : "Monthly Bonus Policy", desc: lang === "ar" ? "الهدف، النسبة، السقف الذهبي" : "Target, percent, golden ceiling",   testId: "settings-bonus-policy" },
      ],
    });
  }

  // Attendance
  const attendanceItems = [];
  if ((isRoot || isManager) && (isRoot || moduleVis.attendance)) {
    attendanceItems.push({ icon: CalendarClock, to: "/admin/attendance", title: lang === "ar" ? "الحضور — إدارة" : "Attendance Hub", desc: lang === "ar" ? "الجدول الشهري، الإجازات، القواعد" : "Monthly grid, leaves, rules", testId: "settings-attendance-admin" });
  }
  if (isEmployee && moduleVis.attendance) {
    attendanceItems.push({ icon: UserCog, to: "/attendance", title: lang === "ar" ? "تسجيل الحضور والانصراف" : "Check-in / Check-out", desc: lang === "ar" ? "بصمة GPS داخل نطاق المكتب" : "GPS check within office range", testId: "settings-attendance-checkin" });
  }
  if (attendanceItems.length) groups.push({ key: "attendance", title: lang === "ar" ? "الحضور" : "Attendance", items: attendanceItems });

  // Personal tools (everyone internal)
  const meItems = [];
  if (user.user_type === "internal") {
    meItems.push({ icon: TrendingUp, to: "/my-earnings", title: lang === "ar" ? "بونصي الشهري" : "My Monthly Bonus", desc: lang === "ar" ? "تابع دخلك وبونصك وتقدّمك نحو الهدف" : "Track income & bonus progress", testId: "settings-my-earnings" });
  }
  // NOTE: "التحقق من الفواتير" (invoice-verify) moved into the Invoices view
  // (sidebar → الفواتير → button "تحقّق") — no longer duplicated here.
  if (meItems.length > 0) {
    groups.push({ key: "me", title: lang === "ar" ? "أدواتي" : "My Tools", items: meItems });
  }

  const css = {
    page: {
      minHeight: "100%", background: theme.bg, color: theme.text,
      padding: `${isDesktop ? 32 : 16}px ${isDesktop ? 32 : 16}px 40px`,
      fontFamily: "'Tajawal','Segoe UI', sans-serif",
      maxWidth: 900, margin: "0 auto",
    },
    header: { marginBottom: 20 },
    title: { fontSize: isDesktop ? 24 : 20, fontWeight: 900, color: theme.text, marginBottom: 6 },
    subtitle: { fontSize: 13, color: theme.textMuted, lineHeight: 1.6 },
    group: { marginBottom: 22 },
    groupTitle: {
      fontSize: 11, fontWeight: 800, color: theme.textMuted,
      textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8,
      paddingInline: 2,
    },
    list: {
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      borderRadius: WS_TOKENS.radius.xl, overflow: "hidden",
    },
    row: {
      display: "grid", gridTemplateColumns: "auto 1fr auto",
      alignItems: "center", gap: 14,
      padding: "14px 16px",
      cursor: "pointer",
      background: "transparent",
      transition: `background ${WS_TOKENS.motion.fast}`,
      textAlign: "start", fontFamily: "inherit", color: "inherit",
      minHeight: WS_TOKENS.touch + 8,
      width: "100%", border: 0,
      borderBlockEnd: `1px solid ${theme.borderSoft}`,
    },
    icon: {
      width: 36, height: 36, borderRadius: 10,
      background: "rgba(212,175,55,0.10)",
      border: `1px solid ${theme.borderAccent}`,
      color: theme.gold,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    },
    body: { minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
    rowTitle: { fontSize: 14, fontWeight: 800, color: theme.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    rowDesc: { fontSize: 12, color: theme.textMuted, lineHeight: 1.5 },
    chevron: { color: theme.textMuted, flexShrink: 0 },
    portalControls: { marginTop: 24 },
  };

  return (
    <div style={css.page} dir={dir} data-testid="admin-settings-page">
      <header style={css.header}>
        <div style={css.title}>{lang === "ar" ? "الإعدادات" : "Settings"}</div>
        <div style={css.subtitle}>
          {lang === "ar"
            ? (isRoot ? "لوحة تحكم شاملة لإدارة النظام والعمليات والمالية."
              : isManager ? "أدوات الإدارة والمالية والأداء والحضور."
              : isAccountant ? "لوحة المالية والأداء."
              : "أدواتك اليومية وحضورك.")
            : "Central hub for system, operations, finance & attendance."}
        </div>
      </header>

      {groups.map((g) => (
        <section key={g.key} style={css.group} data-testid={`settings-group-${g.key}`}>
          <div style={css.groupTitle}>{g.title}</div>
          <div style={css.list}>
            {g.items.map((it) => (
              <button
                key={it.testId}
                type="button"
                onClick={() => navigate(it.to)}
                style={css.row}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                data-testid={it.testId}
              >
                <span style={css.icon}><it.icon size={18} strokeWidth={1.75} /></span>
                <span style={css.body}>
                  <span style={css.rowTitle}>{it.title}</span>
                  <span style={css.rowDesc}>{it.desc}</span>
                </span>
                <ChevronIcon size={16} style={css.chevron} />
              </button>
            ))}
          </div>
        </section>
      ))}

      {isRoot && (
        <div style={css.portalControls} data-testid="settings-portal-controls">
          <PortalControls />
        </div>
      )}
    </div>
  );
}
